import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Card, Result, Button, Spin, Space, Typography } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import API_CONFIG from "../config/api.js";

const { Text } = Typography;

type PayState = "checking" | "paid" | "failed" | "timeout" | "unknown";

const POLL_INTERVAL_MS = 3000;
const MAX_ATTEMPTS = 40; // ~2 minutes

/**
 * Page de retour après un paiement en ligne YengaPay.
 * Le client est redirigé ici depuis la page de paiement hébergée. On interroge
 * le backend (statut mis à jour par le webhook signé) jusqu'à obtenir un état
 * final (payé/échoué) ou expiration.
 */
const PaymentReturn: React.FC = () => {
  const { storeSlug } = useParams<{ storeSlug: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [state, setState] = useState<PayState>("checking");
  const [orderNumber, setOrderNumber] = useState<string>("");
  const attemptsRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const STORE_API = API_CONFIG.PUBLIC_STORE_API;

  useEffect(() => {
    // La même configuration YengaPay peut servir la boutique et les
    // abonnements ELSA. Une référence ELSASUB retourne vers le suivi dédié,
    // sans jamais être interprétée comme un numéro de commande e-commerce.
    let subscriptionReference = "";
    try {
      const candidate =
        searchParams.get("reference") ||
        localStorage.getItem("elsa_subscription_pending_reference") ||
        "";
      if (candidate.startsWith("ELSASUB-")) subscriptionReference = candidate;
    } catch (_) {
      subscriptionReference = "";
    }
    if (subscriptionReference) {
      navigate(
        `/payment?reference=${encodeURIComponent(subscriptionReference)}`,
        { replace: true }
      );
      return;
    }

    // Résoudre le numéro de commande : query (?order= / ?reference=) sinon localStorage
    const fromQuery =
      searchParams.get("order") ||
      searchParams.get("reference") ||
      searchParams.get("order_number");
    let resolved = fromQuery || "";
    if (!resolved) {
      try {
        resolved = localStorage.getItem("yengapay_pending_order") || "";
      } catch (_) {
        resolved = "";
      }
    }

    if (!resolved) {
      setState("unknown");
      return;
    }
    setOrderNumber(resolved);

    let cancelled = false;

    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const response = await fetch(
          `${STORE_API}/${storeSlug}/orders/${encodeURIComponent(
            resolved
          )}/payment-status`
        );
        if (response.ok) {
          const data = await response.json();
          const status = data.payment_status;
          if (status === "paid") {
            if (!cancelled) {
              setState("paid");
              try {
                localStorage.removeItem("yengapay_pending_order");
                localStorage.removeItem("yengapay_pending_store");
                localStorage.removeItem("ecommerce_session_id");
              } catch (_) {
                /* ignore */
              }
            }
            return;
          }
          if (status === "failed" || status === "refunded") {
            if (!cancelled) setState("failed");
            return;
          }
        }
      } catch (_) {
        // erreur réseau : on retentera au prochain tick
      }

      if (cancelled) return;
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setState("timeout");
        return;
      }
      timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeSlug]);

  const goToStore = () => navigate(`/store/${storeSlug}`);

  const containerStyle: React.CSSProperties = {
    padding: "2rem",
    maxWidth: 720,
    margin: "0 auto",
  };

  if (state === "checking") {
    return (
      <div style={containerStyle}>
        <Card style={{ textAlign: "center" }}>
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <Spin size="large" />
            <Text style={{ fontSize: 16 }}>
              Vérification de votre paiement en cours…
            </Text>
            {orderNumber && (
              <Text type="secondary">Commande #{orderNumber}</Text>
            )}
          </Space>
        </Card>
      </div>
    );
  }

  if (state === "paid") {
    return (
      <div style={containerStyle}>
        <Result
          icon={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
          status="success"
          title="Paiement confirmé !"
          subTitle={
            orderNumber
              ? `Votre commande #${orderNumber} a été payée avec succès.`
              : "Votre commande a été payée avec succès."
          }
          extra={[
            <Button type="primary" key="store" onClick={goToStore}>
              Continuer les achats
            </Button>,
          ]}
        />
      </div>
    );
  }

  if (state === "failed") {
    return (
      <div style={containerStyle}>
        <Result
          icon={<CloseCircleOutlined style={{ color: "#ff4d4f" }} />}
          status="error"
          title="Paiement non abouti"
          subTitle={
            orderNumber
              ? `Le paiement de la commande #${orderNumber} a échoué ou a été annulé. Aucun montant n'a été débité.`
              : "Le paiement a échoué ou a été annulé."
          }
          extra={[
            <Button type="primary" key="store" onClick={goToStore}>
              Retourner à la boutique
            </Button>,
          ]}
        />
      </div>
    );
  }

  if (state === "timeout") {
    return (
      <div style={containerStyle}>
        <Result
          icon={<ClockCircleOutlined style={{ color: "#faad14" }} />}
          status="warning"
          title="Paiement en attente de confirmation"
          subTitle={
            orderNumber
              ? `Nous n'avons pas encore reçu la confirmation pour la commande #${orderNumber}. Si vous avez payé, elle sera confirmée sous peu.`
              : "Nous n'avons pas encore reçu la confirmation de votre paiement."
          }
          extra={[
            <Button type="primary" key="store" onClick={goToStore}>
              Retourner à la boutique
            </Button>,
          ]}
        />
      </div>
    );
  }

  // state === "unknown"
  return (
    <div style={containerStyle}>
      <Result
        status="info"
        title="Aucune commande à vérifier"
        subTitle="Nous n'avons pas trouvé de commande associée à ce retour de paiement."
        extra={[
          <Button type="primary" key="store" onClick={goToStore}>
            Retourner à la boutique
          </Button>,
        ]}
      />
    </div>
  );
};

export default PaymentReturn;
